import classNames from 'classnames';
import { get, map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Button, { TYPES } from 'components/Button';
import { TertiaryHeading } from 'components/Headings';
import Image from 'components/ImageV2';
import { Small, Medium, LargeUp } from 'components/MediaQuery';
import { Sub } from 'components/Sub';
import { IMAGE_BG_COLOR } from 'constants/images';
import { largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';

const IMAGE_SIZE = 1000;
const ICON_SIZE = 30;
const ICON_SIZE_LARGE = 60;
class ProductCard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      focused: false,
    };
  }

  updateFocus = focused => {
    this.setState({ focused });
  };

  render() {
    const {
      productName,
      tagline,
      taglineIcon,
      image,
      ctaLinks,
      trackCTAClick,
      productCardIndex,
      categoryIndex,
    } = this.props;
    const { focused } = this.state;

    return (
      <div className="ProductCard">
        <div className="ProductCard__image-container">
          <Image
            className="ProductCard__image"
            src={image.src}
            alt={image.alt}
            width={IMAGE_SIZE}
            height={IMAGE_SIZE}
            transformations={{
              backgroundColor: IMAGE_BG_COLOR,
            }}
          />
          <LargeUp>
            <div
              className={classNames('ProductCard__image__cta-container', {
                'ProductCard__image__cta-container--focused': focused,
              })}
            >
              {map(ctaLinks, (ctaLink, index) => (
                <div key={index} className="ProductCard__cta--large">
                  <Button
                    type={TYPES.INVERSE}
                    linkTo={get(ctaLink, 'url')}
                    onFocus={() => this.updateFocus(true)}
                    onBlur={() => this.updateFocus(false)}
                    isFullWidth
                    onClick={() => {
                      trackCTAClick(ctaLink.url, ctaLink.text, categoryIndex, productCardIndex);
                    }}
                  >
                    {get(ctaLink, 'text')}
                  </Button>
                </div>
              ))}
            </div>
          </LargeUp>
        </div>
        <div className="ProductCard__details">
          <div className="ProductCard__details-copy">
            <div className="ProductCard__details-header-container">
              <TertiaryHeading className="ProductCard__details-header">
                {productName}
              </TertiaryHeading>
            </div>
            <div className="ProductCard__details-description">
              <div className="ProductCard__details-image-container">
                <Image
                  src={taglineIcon.src}
                  alt={taglineIcon.alt}
                  width={ICON_SIZE_LARGE}
                  height={ICON_SIZE_LARGE}
                  className="ProductCard__details-description-image"
                />
              </div>
              <Sub className="ProductCard__details-description-tag">{tagline}</Sub>
            </div>
          </div>
          <div className="ProductCard__details-cta-links">
            <Small>
              {map(ctaLinks, (ctaLink, index) => (
                <div key={index} className="ProductCard__cta--small">
                  <Button
                    type={TYPES.DEFAULT}
                    linkTo={ctaLink.url}
                    isFullWidth
                    isSmall
                    onClick={() => {
                      trackCTAClick(ctaLink.url, ctaLink.text, categoryIndex, productCardIndex);
                    }}
                  >
                    {get(ctaLink, 'text')}
                  </Button>
                </div>
              ))}
            </Small>
            <Medium>
              {map(ctaLinks, (ctaLink, index) => (
                <div key={index} className="ProductCard__cta--small">
                  <Button
                    type={TYPES.DEFAULT}
                    linkTo={ctaLink.url}
                    isFullWidth
                    isSmall
                    onClick={() => {
                      trackCTAClick(ctaLink.url, ctaLink.text, categoryIndex, productCardIndex);
                    }}
                  >
                    {get(ctaLink, 'text')}
                  </Button>
                </div>
              ))}
            </Medium>
          </div>
        </div>
        <style jsx>
          {`
            .ProductCard {
              height: 100%;
              display: flex;
              flex-direction: column;
              flex: 1 0 auto;
              width: 100%;
              box-shadow: 0 ${Theme.get('spacing')}px ${Theme.get('spacing') * 4}px 0
                ${Theme.get('colors-black-20')};
            }

            .ProductCard__image-container {
              width: 100%;
              background-color: ${IMAGE_BG_COLOR};
              position: relative;
              overflow: hidden;
            }

            .ProductCard__image__cta-container {
              height: 100%;
              width: 100%;
              position: absolute;
              top: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              flex-direction: column;
              opacity: 0;
              transition:
                opacity ${Theme.get('transition-duration-ms')}
                  ${Theme.get('transition-timing-ease-out')},
                background-color ${Theme.get('transition-duration-ms')}
                  ${Theme.get('transition-timing-ease-out')};
              padding: 0 ${Theme.get('spacing') * 10}px;
            }

            .ProductCard__image__cta-container:hover,
            .ProductCard__image__cta-container--focused {
              background-color: rgba(0, 0, 0, 0.4);
              opacity: 1;
              transition:
                opacity ${Theme.get('transition-duration-ms')}
                  ${Theme.get('transition-timing-ease-out')},
                background-color ${Theme.get('transition-duration-ms')}
                  ${Theme.get('transition-timing-ease-out')};
            }

            .ProductCard__cta--large,
            .ProductCard__cta--small {
              width: 100%;
              display: inherit;
              margin: ${Theme.get('spacing')}px 0;
            }

            .ProductCard__details {
              padding: ${Theme.get('spacing') * 3}px;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              flex: 1 0 auto;
            }

            @media ${largeUp} {
              .ProductCard__details {
                padding: ${Theme.get('spacing') * 3}px ${Theme.get('spacing') * 5}px;
              }
            }

            .ProductCard__details-header-container {
              border-bottom: 1px solid ${Theme.get('colors-black-20')};
              padding-bottom: ${Theme.get('spacing') * 1.5}px;
              margin-bottom: ${Theme.get('spacing') * 1.5}px;
            }

            @media ${largeUp} {
              .ProductCard__details-header-container {
                margin-bottom: ${Theme.get('spacing') * 2}px;
              }
            }

            .ProductCard__details-description {
              display: flex;
              align-items: center;
            }
          `}
        </style>
        <style jsx global>
          {`
            .ProductCard__cta--small .Button {
              padding: 12px;
              margin: 9px 0;
              font-weight: ${Theme.get('font-weight-semi-bold')};
              font-size: ${Theme.get('font-size-small-px')};
            }

            .ProductCard__cta--large .Button {
              border: none;
              padding: ${Theme.get('spacing') * 2.5}px;
              line-height: normal;
            }

            .ProductCard__image {
              transition: transform ${Theme.get('transition-duration-ms')}
                ${Theme.get('transition-timing-ease-out')};
              width: 100%;
            }

            @media (hover: hover) {
              .ProductCard:hover .ProductCard__image {
                transform: scale(1.05);
              }
            }

            .ProductCard__details-image-container {
              margin-right: ${Theme.get('spacing') * 2}px;
              width: ${ICON_SIZE}px;
            }

            .Sub.ProductCard__price {
              font-size: ${Theme.get('font-size-px')};
            }
          `}
        </style>
      </div>
    );
  }
}

ProductCard.propTypes = {
  productName: PropTypes.string.isRequired,
  tagline: PropTypes.string.isRequired,
  taglineIcon: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
  }).isRequired,
  image: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
  }).isRequired,
  ctaLinks: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ).isRequired,
  trackCTAClick: PropTypes.func.isRequired,
  productCardIndex: PropTypes.number.isRequired,
  categoryIndex: PropTypes.number.isRequired,
};

export default ProductCard;
